import dayjs from 'dayjs';
import { useTheme } from 'styled-components';

import LoadingCircle from '~components/Loading';
import { COMPANY_DETAIL_MENU } from '~constant/company';
import { useGetCompanySales } from '~hooks/query/company/useCompanySales';
import useDropdownSelect from '~hooks/useDropdownSelect';
import { useRouterQuery } from '~hooks/useRouterQuery';
import { CompanyStates, RegisterRef, SalesSource, SalesSourceValue } from '~types/company';

import BlockedInfo from '../BlockedInfo';
import Card from '../Card';
import DropDownSource from '../DropDownSource';
import ErrorSection from '../ErrorSection';
import SalesRate from './SalesRate';
import FinancialChart from './FinancialChart';
import CompanySectionTitle from '../CompanySectionTitle';
import Tooltip from '~components/common/Tooltip';
import ExclamationMark from '~components/Icon/ExclamationMark';
import * as S from './style';

interface Props {
  registerRef: RegisterRef;
  companyStates: CompanyStates;
}

function SalesInfo({ registerRef, companyStates }: Props) {
  const theme = useTheme();
  const companyId = useRouterQuery('companyId');
  const { selectedOption, handleSelectChange } = useDropdownSelect<SalesSourceValue>();
  const salesQuery = useGetCompanySales(companyStates.closed, companyId, selectedOption);

  const cardProps = {
    registerRef,
    title: '매출',
    sectionId: COMPANY_DETAIL_MENU.SALES,
    rightCardHeader: !(
      companyStates.closed ||
      companyStates.deleted ||
      !companyStates.sales ||
      !salesQuery.data
    ) && (
      <DropDownSource
        value={selectedOption || SalesSource[salesQuery.data?.defaultSource]}
        emptyValue={!(selectedOption || salesQuery.data?.defaultSource) ? '정보없음' : undefined}
        options={salesQuery.data?.sourceOption}
        isShowTooltip={false}
        tooltipHelperText={
          salesQuery.data?.activeSales?.updatedAt && salesQuery.data.defaultSource
            ? `${dayjs(salesQuery.data?.activeSales.updatedAt).format('YYYY.MM')} 기준`
            : '매출 출처 :'
        }
        onChange={(value) => {
          handleSelectChange(value as SalesSourceValue);
        }}
      />
    ),
  };

  if (companyStates?.closed || companyStates?.deleted) {
    return null;
  }

  if (!companyStates?.sales) {
    return (
      <Card {...cardProps}>
        <BlockedInfo />
      </Card>
    );
  }

  if (salesQuery.isError) {
    return (
      <Card {...cardProps}>
        <ErrorSection />
      </Card>
    );
  }

  if (salesQuery.isLoading) {
    return (
      <Card {...cardProps}>
        <LoadingCircle />
      </Card>
    );
  }

  return (
    <Card {...cardProps}>
      <S.SalesRateWrapper>
        <SalesRate title={'매출'} sales={salesQuery.data.activeSales.total} type='total' />
        <SalesRate
          title={'1인당 매출액'}
          tooltipText={'매출액 / 인원수'}
          sales={salesQuery.data.activeSales.perPerson}
          type='person'
        />
        <SalesRate
          title={'매출액 대비 임금 비율'}
          tooltipText={'매출액 / (평균임금 X 인원수)'}
          sales={salesQuery.data.activeSales.salesSalaryRate}
          type='salary'
        />
      </S.SalesRateWrapper>

      <S.FinancialChartWrapper>
        <CompanySectionTitle inlineCss={S.cssTitleChart}>
          <S.BadgeNew>NEW</S.BadgeNew>
          별도 재무제표 (금감원)
          <Tooltip
            width={240}
            tooltipText='별도 재무제표에는 종속기업의 손익이 포함되어 있지 않습니다.'
            position='center'>
            <ExclamationMark width={15} color={theme.colors.gray700} />
          </Tooltip>
        </CompanySectionTitle>
        <S.ChartWrapper>
          <FinancialChart companyId={companyId} />
        </S.ChartWrapper>
      </S.FinancialChartWrapper>
    </Card>
  );
}

export default SalesInfo;
